import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useDndContext } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Container from '@ui/container/container';
import Button from '@ui/buttons/button/button';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import OptionalComponent from '@ui/optional-component/optional-component';
import type { ResolvedEmailBlock } from '../../templates/widgets/block-types';
import { WidgetType } from '../../templates/widgets/widget-registry';

function ControlButton({
  icon,
  design,
  disabled,
  onClick,
}: {
  icon: string;
  design: 'LocalBttnSec' | 'LocalBttnCritical';
  disabled?: boolean;
  onClick: (e: React.MouseEvent) => void;
}) {
  return (
    <Button
      width="32px"
      height="32px"
      padding="0"
      design={design}
      borderRadius="100px"
      disabled={disabled}
      disabledOpacity={0.35}
      onClick={onClick}
    >
      <Icon color="var(--global-ico-text-1)" name={icon} size={12} />
    </Button>
  );
}

const BORDER_COLOR = '#FF4EBB';

interface BlockShellProps {
  block: ResolvedEmailBlock;
  isEditMode: boolean;
  // gates the dnd-kit drop target only; arrow buttons show independently of this
  reorderable?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  selected?: boolean;
  onSelect?: (blockId: string | null) => void;
  onDelete?: (blockId: string) => void;
  onMoveUp?: (blockId: string) => void;
  onMoveDown?: (blockId: string) => void;
  children: React.ReactNode;
}

export default function BlockShell({
  block,
  isEditMode,
  reorderable = true,
  isFirst,
  isLast,
  selected,
  onSelect,
  onDelete,
  onMoveUp,
  onMoveDown,
  children,
}: BlockShellProps) {
  const isSelectable = !block.locked;
  const canReorder = isSelectable && !!(onMoveUp || onMoveDown);
  const { attributes, listeners, setNodeRef, transform, transition, isOver, isDragging } = useSortable({
    id: block.id,
    disabled: { draggable: !canReorder, droppable: !isEditMode || !reorderable },
  });
  const [isHovered, setIsHovered] = useState(false);
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const setRefs = (node: HTMLDivElement | null) => {
    nodeRef.current = node;
    setNodeRef(node);
  };
  const { active } = useDndContext();
  const isDraggingNewWidget = !!active?.data.current?.widgetType;
  // tracks the dragged block so it can be marked hovered post-drop
  const activeIdRef = useRef(active?.id);
  if (active?.id !== undefined) {
    activeIdRef.current = active.id;
  }
  const wasActiveRef = useRef(false);
  useEffect(() => {
    const isActiveNow = !!active;
    if (wasActiveRef.current && !isActiveNow) {
      if (activeIdRef.current === block.id) {
        setIsHovered(true);
      }
      activeIdRef.current = undefined;
    }
    wasActiveRef.current = isActiveNow;
  }, [active, block.id]);

  if (!isEditMode) return <>{children}</>;

  const showBorder = isSelectable && (isHovered || selected || isDragging);
  const showControls = isSelectable && (isHovered || selected);
  // compensates for the preview's own CSS scale, which otherwise shrinks drag transforms
  const previewScale = nodeRef.current
    ? parseFloat(getComputedStyle(nodeRef.current).getPropertyValue('--preview-scale')) || 1
    : 1;
  const scaledTransform = transform
    ? { ...transform, x: transform.x / previewScale, y: transform.y / previewScale }
    : transform;
  const dragStyle: CSSProperties = {
    transform: CSS.Transform.toString(scaledTransform),
    transition,
    zIndex: isDragging ? 10 : undefined, // wins the stacking tie against a transiently-isOver sibling
    opacity: isDragging ? 0.4 : undefined,
  };

  const padDivider =
    (block.type === WidgetType.Divider || block.type === WidgetType.Spacer) && (isHovered || isDragging);

  return (
    <div
      data-testid="block-shell-ref"
      ref={setRefs}
      className={`position-relative${showBorder || isOver ? ' z-1' : ''}`}
      style={{
        width: '100%',
        marginTop: padDivider ? -12 : undefined,
        marginBottom: padDivider ? -12 : undefined,
        paddingBottom: padDivider ? 12 : undefined,
        paddingTop: padDivider ? 12 : undefined, // gives thin blocks (dividers, spacers) a usable hit/drop area
        display: 'flex',
        alignItems: 'center',
        outline: showBorder ? `2px solid ${BORDER_COLOR}` : '2px solid transparent',
        ...dragStyle,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={
        isSelectable
          ? (e) => {
              e.stopPropagation();
              onSelect?.(block.id);
            }
          : undefined
      }
    >
      {children}
      {isOver && isDraggingNewWidget && (
        <Container
          className="position-absolute left-0 right-0 bottom--3 height-4 radius-2 pointer-events-none"
          background={BORDER_COLOR}
        />
      )}
      <OptionalComponent display={showControls}>
        <Container className="position-absolute top-8 right-8 flex ai-center gap-4">
          {canReorder && (
            <Button
              width="32px"
              height="32px"
              padding="0"
              design="LocalBttnSec"
              borderRadius="100px"
              className="cursor-grab"
              onClick={(e) => e.stopPropagation()}
              {...attributes}
              {...listeners}
            >
              <Icon color="var(--global-ico-text-1)" name={ICONS.DRAG_HANDLE} size={12} />
            </Button>
          )}
          {(onMoveUp || onMoveDown) && (
            <>
              <ControlButton
                icon={ICONS.ARROW_UP}
                design="LocalBttnSec"
                disabled={isFirst}
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveUp?.(block.id);
                }}
              />
              <ControlButton
                icon={ICONS.ARROW_DOWN}
                design="LocalBttnSec"
                disabled={isLast}
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveDown?.(block.id);
                }}
              />
            </>
          )}
          {onDelete && (
            <ControlButton
              icon={ICONS.TRASH}
              design="LocalBttnCritical"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(block.id);
              }}
            />
          )}
        </Container>
      </OptionalComponent>
    </div>
  );
}
